/**
 * Continue step of the match-sheet import (EPIC-112 D6, EPIC-116 D2): turn a
 * resolved `FillSide` into wizard line-up selections. Rosters are re-fetched
 * first (a failed fetch aborts with nothing created; `precheckLineup` runs
 * for BOTH sides before `createLineup` creates anything), the clash and duplicate
 * checks re-run on the fresh data, and a player created by an earlier attempt
 * is linked by its remembered id instead of being created again.
 */
import type { LineupPlayerSelection } from "../components/lineup/useLineupDnd";
import type { FillSide, RosterEntry } from "./matchSheetImportReview";
import { findRosterClashes } from "./matchSheetImportReview";
import type { QuickCreatedPlayer } from "./matchSheetImportApi";

export type { RosterEntry };

export interface MaterializeDeps {
  /** Throws on failure; never returns [] for an error. */
  fetchRoster: (teamId: string) => Promise<RosterEntry[]>;
  quickCreate: (
    teamId: string,
    body: { name: string; jersey_number: number; position: string },
  ) => Promise<QuickCreatedPlayer>;
  /** Called right after each successful create so the review can remember it. */
  onCreated?: (sheetIndex: 0 | 1, rowKey: string, playerId: string) => void;
}

export interface ProvisionalErrorInfo {
  kind: "server" | "clash" | "duplicate";
  number?: number;
  name?: string;
}

export class ProvisionalCreateError extends Error {
  readonly rowKey: string;
  readonly sheetIndex: 0 | 1;
  readonly info: ProvisionalErrorInfo;
  constructor(
    message: string,
    rowKey: string,
    sheetIndex: 0 | 1,
    info: ProvisionalErrorInfo = { kind: "server" },
  ) {
    super(message);
    this.name = "ProvisionalCreateError";
    this.rowKey = rowKey;
    this.sheetIndex = sheetIndex;
    this.info = info;
  }
}

export class RosterLoadError extends Error {
  readonly teamId: string;
  constructor(message: string, teamId: string) {
    super(message);
    this.name = "RosterLoadError";
    this.teamId = teamId;
  }
}

const errorText = (err: unknown): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return (err as Error)?.message ?? "Request failed";
};

async function loadRoster(
  teamId: string,
  fetchRoster: MaterializeDeps["fetchRoster"],
) {
  try {
    return await fetchRoster(teamId);
  } catch (err) {
    throw new RosterLoadError(errorText(err), teamId);
  }
}

export interface MaterializeResult {
  lineup: LineupPlayerSelection[];
  /** Fresh roster, fetched after any creation. */
  roster: RosterEntry[];
}

/** Output of the precheck phase: nothing has been created yet. */
export interface LineupPlan {
  side: FillSide;
  /** Roster fetched during the precheck (before any creation). */
  roster: RosterEntry[];
}

/**
 * Precheck phase (EPIC-116 D2): fetch the roster, then run the clash and
 * duplicate checks on that fresh data. Creates nothing, so the caller can
 * precheck BOTH sides before any quick-create runs.
 */
export async function precheckLineup(
  side: FillSide,
  deps: Pick<MaterializeDeps, "fetchRoster">,
): Promise<LineupPlan> {
  const idx = side.sheetIndex;
  const roster = await loadRoster(side.teamId, deps.fetchRoster);

  const toCreate = side.lineup.filter(
    (r) => r.playerId == null && !r.createdPlayerId,
  );
  const clashes = findRosterClashes(
    toCreate.map((r) => ({ key: r.key, number: r.number })),
    roster,
  );
  for (const row of toCreate) {
    const c = clashes.get(row.key);
    if (c) {
      throw new ProvisionalCreateError(
        `Number ${c.number} is already used by ${c.name}`,
        row.key,
        idx,
        { kind: "clash", number: c.number, name: c.name },
      );
    }
  }
  const seen = new Map<string, string>();
  for (const row of side.lineup) {
    const id = row.playerId ?? row.createdPlayerId;
    if (!id) continue;
    if (seen.has(id)) {
      throw new ProvisionalCreateError(
        `Rows ${seen.get(id)} and ${row.key} are the same player`,
        row.key,
        idx,
        { kind: "duplicate", name: row.fullName },
      );
    }
    seen.set(id, row.key);
  }
  return { side, roster };
}

/**
 * Create phase: quick-create the provisional rows of a prechecked plan, then
 * re-fetch the roster so the board draws from fresh data.
 */
export async function createLineup(
  plan: LineupPlan,
  deps: MaterializeDeps,
): Promise<MaterializeResult> {
  const { side } = plan;
  const idx = side.sheetIndex;
  let roster = plan.roster;
  const out: LineupPlayerSelection[] = [];
  let created = 0;
  for (const row of side.lineup) {
    let playerId = row.playerId ?? row.createdPlayerId ?? null;
    let playerName = row.playerName;
    if (playerId == null) {
      try {
        const res = await deps.quickCreate(side.teamId, {
          name: row.fullName,
          jersey_number: row.number,
          position: row.position,
        });
        playerId = res.player_id;
        playerName = res.name || row.fullName;
        created += 1;
        deps.onCreated?.(idx, row.key, res.player_id);
      } catch (err) {
        throw new ProvisionalCreateError(errorText(err), row.key, idx);
      }
    }
    out.push({
      player_id: playerId,
      player_name: playerName,
      position: row.position,
      jersey_number: row.number,
      is_starter: row.isStarter,
      is_captain: row.captain,
    });
  }
  if (created > 0) roster = await loadRoster(side.teamId, deps.fetchRoster);
  return { lineup: out, roster };
}

/** One side, both phases. The wizard hand-off prechecks both sides first. */
export async function materializeLineup(
  side: FillSide,
  deps: MaterializeDeps,
): Promise<MaterializeResult> {
  return createLineup(await precheckLineup(side, deps), deps);
}
